/**
 * Property Tab — Subject Property Information + Valuation
 * Consumes API shape: { loan: LoanInfoViewModel, property: PropertyViewModel }
 */
import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material';
import HouseIcon from '@mui/icons-material/House';
import EditableField from '../../../components/EditableField';
import { US_STATES } from '../../../services/mock/urlaEnumerations';
import SectionHeader from '../../../components/shared/SectionHeader';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SummarizeIcon from '@mui/icons-material/Summarize';

import { fmtDollar as fmt, fmtPct as pct } from '../../../utils/formatters';

import DataItem from '../../../components/shared/DataItem';

const PROPERTY_TYPES = [
  { value: 'SingleFamily', label: 'Single Family' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Condominium', label: 'Condominium' },
  { value: 'Cooperative', label: 'Co-op' },
  { value: 'TwoToFourUnitProperty', label: '2-4 Unit' },
  { value: 'ManufacturedHome', label: 'Manufactured' },
];

const OCCUPANCY_TYPES = [
  { value: 'PrimaryResidence', label: 'Primary Residence' },
  { value: 'SecondHome', label: 'Second Home' },
  { value: 'Investment', label: 'Investment' },
];

const PropertyTab = ({ loanDetail, isEditMode = false }) => {
  if (!loanDetail) return null;
  const prop = loanDetail.property;
  const l = loanDetail.loan || {};

  if (!prop) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <HouseIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography variant="h6" color="text.secondary">No property information</Typography>
        <Typography variant="body2" color="text.secondary">Property data is not available for this loan.</Typography>
      </Box>
    );
  }

  const addr = prop.address || {};
  const sp = isEditMode ? 2 : 1;
  const showSidebar = !isEditMode;

  const fullAddress = [addr.street, addr.unit].filter(Boolean).join(' ') + (addr.city ? `, ${addr.city}` : '') + (addr.state ? `, ${addr.state}` : '') + (addr.zip ? ` ${addr.zip}` : '');
  const propTypeLabel = PROPERTY_TYPES.find(t => t.value === prop.propertyType)?.label || prop.propertyType;
  const occLabel = OCCUPANCY_TYPES.find(t => t.value === (prop.occupancy || l.intendedUsage))?.label || prop.occupancy || l.intendedUsage;
  const purchasePrice = prop.purchasePrice || l.purchasePrice;
  const appraisedValue = prop.appraisedValue || l.appraisedValue;

  const propertySidebar = showSidebar ? (
    <Box sx={{ position: 'sticky', top: 16 }}>
      <Card variant="outlined" sx={{ bgcolor: 'background.default' }}>
        <CardContent sx={{ pb: '12px !important' }}>
          <SectionHeader title="Property Snapshot" icon={<SummarizeIcon fontSize="small" />} sx={{ mb: 1 }} />

          <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
              <LocationOnIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">Subject Property</Typography>
            </Stack>
            <Typography variant="body2" fontWeight={600}>{fullAddress || '—'}</Typography>
            {addr.county && <Typography variant="caption" color="text.secondary">{addr.county} County</Typography>}
          </Box>

          <Box sx={{ pl: 0.5 }}>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">Type</Typography>
              <Typography variant="caption" fontWeight={600}>{propTypeLabel || '—'}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">Occupancy</Typography>
              <Typography variant="caption" fontWeight={600}>{occLabel || '—'}</Typography>
            </Stack>
            {prop.numberOfUnits && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Units</Typography>
                <Typography variant="caption" fontWeight={600}>{prop.numberOfUnits}</Typography>
              </Stack>
            )}
            {prop.yearBuilt && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Year Built</Typography>
                <Typography variant="caption" fontWeight={600}>{prop.yearBuilt}</Typography>
              </Stack>
            )}
            {prop.bedroomCount != null && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Bed / Bath</Typography>
                <Typography variant="caption" fontWeight={600}>{prop.bedroomCount}{prop.bathroomCount != null ? ` / ${prop.bathroomCount}` : ''}</Typography>
              </Stack>
            )}
            {prop.livingAreaSquareFeet != null && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Living Area</Typography>
                <Typography variant="caption" fontWeight={600}>{Number(prop.livingAreaSquareFeet).toLocaleString()} sq ft</Typography>
              </Stack>
            )}
          </Box>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider', pl: 0.5 }}>
            {purchasePrice != null && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Purchase Price</Typography>
                <Typography variant="caption" fontWeight={600}>{fmt(purchasePrice)}</Typography>
              </Stack>
            )}
            {appraisedValue != null && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Appraised Value</Typography>
                <Typography variant="caption" fontWeight={600}>{fmt(appraisedValue)}</Typography>
              </Stack>
            )}
            {l.loanAmount != null && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Loan Amount</Typography>
                <Typography variant="caption" fontWeight={700}>{fmt(l.loanAmount)}</Typography>
              </Stack>
            )}
            {l.downPaymentAmount != null && (
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
                <Typography variant="caption" color="text.secondary">Down Payment</Typography>
                <Typography variant="caption" fontWeight={600}>{fmt(l.downPaymentAmount)}{l.downPaymentPercent != null ? ` (${l.downPaymentPercent.toFixed(1)}%)` : ''}</Typography>
              </Stack>
            )}
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="caption" fontWeight={700} color={l.ltvRatio > 80 ? 'warning.main' : 'success.main'}>LTV Ratio</Typography>
              <Typography variant="caption" fontWeight={700} color={l.ltvRatio > 80 ? 'warning.main' : 'success.main'}>{pct(l.ltvRatio)}</Typography>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  ) : null;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: showSidebar ? 8 : 12 }}>
        <Grid container spacing={2}>
          {/* Address */}
          <Grid size={12}>
            <Card variant="outlined">
              <CardContent sx={{ pb: 1 }}>
                <SectionHeader title="Property Address" icon={<LocationOnIcon fontSize="small" />} />
                <Grid container spacing={sp}>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <EditableField label="Street Address" value={addr.street} name="property.address.street" isEditMode={isEditMode} required={isEditMode} placeholder="123 Main St" />
                    {!isEditMode && !addr.street && <DataItem label="Address" bold>—</DataItem>}
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <EditableField label="Unit" value={addr.unit} name="property.address.unit" isEditMode={isEditMode} placeholder="4B" />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <EditableField label="City" value={addr.city} name="property.address.city" isEditMode={isEditMode} required={isEditMode} placeholder="Los Angeles" />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <EditableField label="State" value={addr.state} name="property.address.state" isEditMode={isEditMode} options={isEditMode ? US_STATES : null} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3, md: 2 }}>
                    <EditableField label="ZIP" value={addr.zip} name="property.address.zip" isEditMode={isEditMode} required={isEditMode} placeholder="90001" />
                  </Grid>
                  {!isEditMode && addr.county && <Grid size={{ xs: 6, sm: 3, md: 2 }}><DataItem label="County">{addr.county}</DataItem></Grid>}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Property Details */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ pb: 1 }}>
                <SectionHeader title="Property Details" icon={<HomeWorkIcon fontSize="small" />} />
                <Grid container spacing={sp}>
                  <Grid size={6}>
                    {isEditMode ? (
                      <EditableField label="Property Type" value={prop.propertyType} name="property.propertyType" isEditMode options={PROPERTY_TYPES} required />
                    ) : (
                      <Box sx={{ py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Property Type</Typography>
                        <Box><Chip label={prop.propertyType || '—'} size="small" variant="outlined" color="info" /></Box>
                      </Box>
                    )}
                  </Grid>
                  <Grid size={6}>
                    <EditableField label="Occupancy" value={prop.occupancy || (!isEditMode ? l.intendedUsage : undefined)} name="property.occupancy" isEditMode={isEditMode} options={isEditMode ? OCCUPANCY_TYPES : null} required={isEditMode} />
                  </Grid>
                  <Grid size={4}>
                    <EditableField label="Units" value={prop.numberOfUnits} name="property.numberOfUnits" isEditMode={isEditMode} type={isEditMode ? 'number' : 'text'} placeholder="1" />
                  </Grid>
                  <Grid size={4}>
                    <EditableField label="Year Built" value={prop.yearBuilt} name="property.yearBuilt" isEditMode={isEditMode} type={isEditMode ? 'number' : 'text'} placeholder="2005" />
                  </Grid>
                  <Grid size={4}>
                    <EditableField label="Bedrooms" value={prop.bedroomCount} name="property.bedroomCount" isEditMode={isEditMode} type={isEditMode ? 'number' : 'text'} />
                  </Grid>
                  <Grid size={4}>
                    <EditableField label="Bathrooms" value={prop.bathroomCount} name="property.bathroomCount" isEditMode={isEditMode} type={isEditMode ? 'number' : 'text'} />
                  </Grid>
                  {!isEditMode && prop.livingAreaSquareFeet != null && <Grid size={4}><DataItem label="Living Area">{Number(prop.livingAreaSquareFeet).toLocaleString()} sq ft</DataItem></Grid>}
                  {!isEditMode && prop.lotSizeSquareFeet != null && <Grid size={4}><DataItem label="Lot Size">{Number(prop.lotSizeSquareFeet).toLocaleString()} sq ft</DataItem></Grid>}
                  {!isEditMode && prop.constructionMethod && <Grid size={4}><DataItem label="Construction">{prop.constructionMethod}</DataItem></Grid>}
                  {!isEditMode && prop.attachmentType && <Grid size={4}><DataItem label="Attachment">{prop.attachmentType}</DataItem></Grid>}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Valuation */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ pb: 1 }}>
                <SectionHeader title="Valuation" icon={<AssessmentIcon fontSize="small" />} />
                <Grid container spacing={sp}>
                  <Grid size={6}>
                    <EditableField label="Purchase Price" value={prop.purchasePrice || (!isEditMode ? l.purchasePrice : undefined)} name="property.purchasePrice" isEditMode={isEditMode} type="currency" required={isEditMode && l?.loanPurpose === 'Purchase'} placeholder="500,000" />
                  </Grid>
                  <Grid size={6}>
                    <EditableField label="Appraised Value" value={prop.appraisedValue || (!isEditMode ? l.appraisedValue : undefined)} name="property.appraisedValue" isEditMode={isEditMode} type="currency" />
                  </Grid>
                  <Grid size={6}>
                    <EditableField label="Estimated Value" value={prop.estimatedValue} name="property.estimatedValue" isEditMode={isEditMode} type="currency" />
                  </Grid>
                  {!isEditMode && (
                    <Grid size={6}>
                      <DataItem label="Down Payment">
                        {fmt(l.downPaymentAmount)}
                        {l.downPaymentPercent != null && <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>({l.downPaymentPercent.toFixed(1)}%)</Typography>}
                      </DataItem>
                    </Grid>
                  )}
                  {!isEditMode && <Grid size={6}><DataItem label="Loan Amount" bold>{fmt(l.loanAmount)}</DataItem></Grid>}
                  <Grid size={6}>
                    <Box sx={{ py: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">LTV Ratio</Typography>
                      <Box><Chip label={pct(l.ltvRatio)} size="small" color={l.ltvRatio > 80 ? 'warning' : 'success'} variant="outlined" /></Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      {showSidebar && (
        <Grid size={{ xs: 12, lg: 4 }} sx={{ display: { xs: 'none', lg: 'block' } }}>
          {propertySidebar}
        </Grid>
      )}
    </Grid>
  );
};

export default PropertyTab;
